import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Layers,
  Grid,
  Plus,
  ArrowRight,
  TrendingUp,
  ShoppingBag,
  List,
  Tags,
  Activity
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

import { message } from 'antd';
import { productsApi } from '../services/api';

const Products = () => {
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalKits: 0,
    totalCategories: 0,
    activeProducts: 0
  });
  const [loading, setLoading] = useState(true);
  const [kitApiStatus, setKitApiStatus] = useState(null); // null=checking, true=ok, false=down

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const [prodRes, kitRes, catRes, healthRes] = await Promise.all([
          productsApi.getAllProducts(),
          productsApi.getAllKits(),
          productsApi.getAllProductCategories(),
          productsApi.getKitHealth().catch(() => null)  // API 39: Kit Health Check
        ]);

        const products = Array.isArray(prodRes) ? prodRes : (prodRes.data || []);
        const kits = Array.isArray(kitRes) ? kitRes : (kitRes.data || []);
        const categories = Array.isArray(catRes) ? catRes : (catRes.data || []);

        const activeCount = products.filter(p => p.isActive !== false && p.status !== false).length;

        setStats({
          totalProducts: products.length,
          totalKits: kits.length,
          totalCategories: categories.length,
          activeProducts: activeCount
        });

        // API 39 result
        setKitApiStatus(healthRes !== null);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setKitApiStatus(false);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const quickActions = [
    { title: 'Add Product', icon: Plus, path: '/products/add', desc: 'Create a new single product', style: { backgroundColor: currentTheme.color } },
    { title: 'View Inventory', icon: List, path: '/products/list', desc: 'Manage all products and kits', color: 'bg-indigo-500' },
    { title: 'Categories', icon: Tags, path: '/products/category', desc: 'Manage product groupings', color: 'bg-purple-500' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: currentTheme.color }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Welcome Header */}
      <div className="relative overflow-hidden rounded-3xl p-8 text-white shadow-xl" style={{ background: `linear-gradient(to right, ${currentTheme.color}, #15803d)` }}>
        <div className="relative z-10">
          <h1 className="text-3xl font-bold mb-2">Product Management Hub</h1>
          <p className="opacity-90 max-w-lg">
            Welcome to the central command for your inventory. Manage categories, track kits, and keep your product catalog up to date.
          </p>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-[-20%] right-[-10%] w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-[-20%] left-[-5%] w-48 h-48 rounded-full blur-2xl" style={{ backgroundColor: `${currentTheme.color}33` }}></div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Total Products', val: stats.totalProducts, icon: Package, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Total Kits/Combos', val: stats.totalKits, icon: Layers, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Categories', val: stats.totalCategories, icon: Grid, color: 'text-teal-600', bg: 'bg-teal-50' },
          { label: 'Active Status', val: stats.activeProducts, icon: Activity, color: 'text-green-600', bg: 'bg-green-50' },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl ${stat.bg} ${stat.color} transition-transform group-hover:scale-110`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <TrendingUp className="w-4 h-4 text-gray-300" />
            </div>
            <p className="text-gray-500 text-sm font-medium">{stat.label}</p>
            <h3 className="text-2xl font-bold text-gray-800">{stat.val}</h3>
          </div>
        ))}
      </div>

      {/* Quick Actions & Navigation */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Actions */}
        <div className="lg:col-span-2 space-y-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" style={{ color: currentTheme.color }} />
            Core Inventory Management
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.map((action, i) => (
              <button
                key={i}
                onClick={() => navigate(action.path)}
                className="group p-6 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all text-left relative overflow-hidden"
                style={{ borderBottomColor: i === 0 ? currentTheme.color : undefined }}
              >
                <div className="w-12 h-12 text-white rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:rotate-12 transition-transform" style={{ backgroundColor: action.style?.backgroundColor || (action.color === 'bg-indigo-500' ? '#6366f1' : '#a855f7') }}>
                  <action.icon className="w-6 h-6" />
                </div>
                <h4 className="font-bold text-gray-800 mb-1">{action.title}</h4>
                <p className="text-xs text-gray-500">{action.desc}</p>
                <div className="absolute bottom-4 right-4 text-gray-300 transition-all group-hover:translate-x-1" style={{ color: i === 0 ? currentTheme.color : undefined }}>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Side Info / Recent Activity Mockup */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Activity className="w-5 h-5" style={{ color: currentTheme.color }} />
            Inventory Health
          </h3>
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Active Listing Ratio</span>
              <span className="text-sm font-bold" style={{ color: currentTheme.color }}>
                {stats.totalProducts > 0 ? Math.round((stats.activeProducts / stats.totalProducts) * 100) : 0}%
              </span>
            </div>
            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{ width: `${stats.totalProducts > 0 ? (stats.activeProducts / stats.totalProducts) * 100 : 0}%`, backgroundColor: currentTheme.color }}
              ></div>
            </div>

            <div className="pt-4 border-t border-gray-50 space-y-3">
              {/* API 39 – Kit Health Check */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Kit API Status</span>
                {kitApiStatus === null ? (
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-400">
                    <span className="w-2 h-2 bg-gray-300 rounded-full animate-pulse" />
                    Checking...
                  </span>
                ) : kitApiStatus ? (
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-green-600 bg-green-50 px-2.5 py-1 rounded-full border border-green-200">
                    <span className="w-2 h-2 bg-green-500 rounded-full" />
                    Operational
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-red-600 bg-red-50 px-2.5 py-1 rounded-full border border-red-200">
                    <span className="w-2 h-2 bg-red-500 rounded-full" />
                    Unavailable
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 leading-relaxed italic">
                Pro Tip: Ensure all products have associated categories for better visibility in the B2C marketplace.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
